import React from 'react';
import {
    Drawer, Box, Typography, List, ListItem, ListItemText, Chip, Divider, IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { FestivalBonusExclusionReason, IFestivalBonusPreview } from '../../interfaces/payroll.interface';

const REASON_LABELS: Record<FestivalBonusExclusionReason, string> = {
    NO_JOINING_DATE: 'No joining date on record',
    UNDER_ONE_YEAR: 'Under one year of service',
    NO_BASIC_COMPONENT: 'No basic salary component assigned',
    NO_PIECE_WAGE_HISTORY: 'No piece-rate wage history to base the bonus on',
};

interface Props {
    open: boolean;
    onClose: () => void;
    loading: boolean;
    preview: IFestivalBonusPreview | null;
}

// Eligibility preview drawer (spec §2.1/§3). Advisory only — read-only and repeatable; not
// persisted anywhere, so a joiningDate or salary edit made after this is shown can change what
// generation actually does.
const FestivalBonusPreviewDrawer: React.FC<Props> = ({ open, onClose, loading, preview }) => {
    return (
        <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ 'data-help-page': 'payroll.festivalPreview.page' } as any}>
            <Box sx={{ width: 380, p: 3 }} role="presentation">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="h6">Eligibility Preview</Typography>
                    <IconButton data-help="payroll.festivalPreview.close" onClick={onClose} aria-label="Close preview" size="small">
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>

                {loading && <Typography variant="body2">Loading preview...</Typography>}

                {!loading && preview && (
                    <>
                        <Chip
                            color="success"
                            label={`${preview.eligibleCount} eligible employee${preview.eligibleCount === 1 ? '' : 's'}`}
                            sx={{ mb: 2 }}
                        />
                        <Divider sx={{ mb: 1 }} />
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Excluded ({preview.excluded.length})
                        </Typography>
                        {preview.excluded.length === 0 && (
                            <Typography variant="body2" color="text.secondary">
                                No excluded employees.
                            </Typography>
                        )}
                        <List dense>
                            {preview.excluded.map((e) => (
                                <ListItem key={e.userId} disableGutters>
                                    <ListItemText
                                        primary={e.employeeName ?? e.username ?? e.userId}
                                        secondary={REASON_LABELS[e.reason] ?? e.reason}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </>
                )}

                {!loading && !preview && (
                    <Typography variant="body2" color="text.secondary">
                        Failed to load the preview.
                    </Typography>
                )}
            </Box>
        </Drawer>
    );
};

export default FestivalBonusPreviewDrawer;
